/**
 * "Redo the kit with the new release date" (owner request 2026-10-20, option A). A kit asset bakes
 * its texts (the release date, "Pre-save now", the countdown, the post caption) into its stored plan
 * when it is made. Each made kit asset stores the release date that its texts used
 * (`KitAssetModel.releaseDateRef`). When the song has another release date now, the asset shows the
 * old date, and the Kit tab offers one free action that writes the date texts again and renders a
 * new version.
 *
 * Option A: from the release day on, an asset whose purpose is before the release (a pre-save
 * Story, a countdown, an announce post) is not made again. The Kit tab lists it in a closed "Before
 * release" group. Before the release day a date change makes it again too, with the new countdown.
 *
 * The contract of the two routes (music24-api `src/kit/dateRedo/`):
 * - `GET  /accounts/:accountId/release-kits/:catalogItemUuid/date-redo` → `DateRedoState`
 * - `POST /accounts/:accountId/release-kits/:catalogItemUuid/date-redo` with `DateRedoRequest`:
 *   `dryRun: true` → 200 `DateRedoState`; else 201 `DateRedoResult`. The redo is free.
 *
 * The file imports only `coverRedo.ts`, `kitText.ts` and `studioThemes.ts`, so every project can use it.
 */
import type { CoverRedoKind, CoverRedoPost, CoverRedoRun } from './coverRedo';
import { capitalizeKitWord, KIT_WEEKDAY_CAPTION_FORMS, kitWords, type KitTextLocale } from './kitText';
import type { StudioThemeId } from './studioThemes';

/** The reference of a song with no release date. A dated reference is the `YYYY-MM-DD` of the date. */
export const NO_RELEASE_DATE_REF = 'none';

/** The reference that an asset stores for the release date its texts used. Pure. */
export function releaseDateRefOf(releaseDate: string | null): string {
  const match = releaseDate === null ? null : /^\d{4}-\d{2}-\d{2}/.exec(releaseDate);
  return match === null ? NO_RELEASE_DATE_REF : match[0];
}

/**
 * The asset types whose texts can name the release date: the Stories and feed posts, the countdown,
 * the banners, the link preview, the lyric card and the short videos with a CTA. The covers, the
 * Canvas, the motion artwork, the Spotify header, the thumbnails and the full videos show no date.
 */
export const DATE_TEXT_THEME_IDS: ReadonlyArray<StudioThemeId> = [
  'story',
  'feedPost',
  'countdown',
  'banners',
  'linkPreview',
  'lyricCard',
  'lyricMoment',
  'artworkInMotion',
];

/** True for an asset type whose texts can name the release date. Pure. */
export function themeShowsReleaseDate(themeId: string): boolean {
  return (DATE_TEXT_THEME_IDS as readonly string[]).includes(themeId);
}

/** The Story moments whose purpose is before the release day. */
const BEFORE_RELEASE_MOMENTS: ReadonlyArray<string> = ['preSave', 'countdown', 'announce'];

/**
 * True when the purpose of an asset is before the release day (option A): a countdown, and a Story
 * or a feed post whose moment is the pre-save, a countdown day or the announce. The banners, the
 * link preview, the lyric card and the short videos stay right after the release (their status
 * becomes "Out now" / "Listen now"). Pure.
 */
export function isBeforeReleasePurpose(input: {
  themeId: string;
  /** The Studio moment of the params (`params.moment`), if any. */
  paramsMoment?: string | null;
  /** The moment of the stored Story texts (`plan.story.moment`), if any. */
  storyMoment?: string | null;
}): boolean {
  if (input.themeId === 'countdown') {
    return true;
  }
  if (input.themeId !== 'story' && input.themeId !== 'feedPost') {
    return false;
  }
  const moment = input.paramsMoment ?? input.storyMoment ?? null;
  return moment !== null && BEFORE_RELEASE_MOMENTS.includes(moment);
}

/** True from the release day on (`today` and the date as `YYYY-MM-DD`). A song with no date is not released. Pure. */
export function isReleaseDayPassed(releaseDate: string | null, today: string): boolean {
  return releaseDate !== null && releaseDate.slice(0, 10) <= today.slice(0, 10);
}

/* ----------------------------------------------------------------------------------------------
 * Date mentions in a text ("1 Nov", "Sun 1 Nov", "November 1st", "2026-11-01"). The builders write
 * "1 Nov" and "Sun 1 Nov"; a caption of the director can use the other forms. A kit in another
 * language (`kitText.ts`, owner request 2026-10-29) writes its own forms ("16. Okt.", "pt. 16 paź",
 * "16 de octubre"): the reads below take the language of the plan (`plan.textLocale`) and match
 * those forms too. English forms always match: an old caption is English.
 * -------------------------------------------------------------------------------------------- */

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH =
  '(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)';
const WEEKDAY = '(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(day|sday|nesday|rsday|urday)?,?\\s+)?';
const DAY = '(\\d{1,2})(st|nd|rd|th)?';

/** "Sun 1 Nov", "1st of November". */
const DAY_FIRST = new RegExp(`\\b${WEEKDAY}${DAY}\\s+(?:of\\s+)?${MONTH}\\b`, 'gi');
/** "Nov 1", "Sunday, November 1st". */
const MONTH_FIRST = new RegExp(`\\b${WEEKDAY}${MONTH}\\s+${DAY}\\b`, 'gi');
/** "2026-11-01". */
const ISO = /\b(\d{4})-(\d{2})-(\d{2})\b/g;

/** A day of the year with no year: `MM-DD` ("11-01"). */
export type DayMonth = string;

function monthIndexOf(word: string): number {
  const prefix = word.slice(0, 3).toLowerCase();
  return MONTH_SHORT.findIndex((month) => month.toLowerCase() === prefix);
}

function dayMonthOf(month: number, day: number): DayMonth | null {
  if (month < 0 || month > 11 || day < 1 || day > 31) {
    return null;
  }
  return `${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The day and the month of a `YYYY-MM-DD` date, or null. Pure. */
export function dayMonthOfDate(releaseDate: string | null): DayMonth | null {
  const match = releaseDate === null ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(releaseDate);
  return match === null ? null : dayMonthOf(Number(match[2]) - 1, Number(match[3]));
}

/** The date of a reference (`releaseDateRefOf`), or null for `none` and an unknown value. Pure. */
export function dateOfRef(ref: string | null): string | null {
  return ref !== null && /^\d{4}-\d{2}-\d{2}$/.test(ref) ? ref : null;
}

interface Mention {
  index: number;
  length: number;
  dayMonth: DayMonth;
  /** Rewrites the mention for another date, in its own form. */
  rewrite: (releaseDate: string) => string;
}

function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return 'th';
  }
  return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
}

function weekdayOf(releaseDate: string): number {
  const [year, month, day] = releaseDate.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)).getUTCDay();
}

/** The parts of a date in the form of a mention: the weekday, the day (with its suffix), the month. */
function dateParts(
  releaseDate: string,
  form: { weekday: string | undefined; longWeekday: boolean; suffix: boolean; longMonth: boolean }
): { weekday: string; day: string; month: string } {
  const month = Number(releaseDate.slice(5, 7)) - 1;
  const day = Number(releaseDate.slice(8, 10));
  const weekdayIndex = weekdayOf(releaseDate);
  let weekday = '';
  if (form.weekday !== undefined) {
    weekday = `${(form.longWeekday ? WEEKDAY_LONG : WEEKDAY_SHORT)[weekdayIndex] ?? ''} `;
  }
  return {
    weekday,
    day: `${day}${form.suffix ? ordinal(day) : ''}`,
    month: (form.longMonth ? MONTH_LONG : MONTH_SHORT)[month] ?? '',
  };
}

const bareWord = (word: string): string => word.toLocaleLowerCase().replace(/\.$/, '');
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A weekday word of a language: its index (Sunday first) and the list of its form. */
interface WeekdayWord {
  index: number;
  /** The list that the word comes from: the redo writes the new weekday from the same list. */
  forms: readonly string[];
  /** True for the short weekday of a day label (`weekdaysShort`). */
  short: boolean;
}

/** A month word of a language: its index (January first) and its form. */
interface MonthWord {
  index: number;
  long: boolean;
  /** True when the word of the list ends with a dot ("nov."): a dot after it is part of the word. */
  dotted: boolean;
}

interface LocaleDateWords {
  /** The month and weekday words of the language, with no trailing dot, lower case, one space. */
  months: Map<string, MonthWord>;
  weekdays: Map<string, WeekdayWord>;
  /** "Fr., 16. Okt.", "16 de octubre", "pt. 16 paź", "w sobotę 17 października": an optional weekday, the day, the month. */
  pattern: RegExp;
}

const localeDateWordsCache = new Map<KitTextLocale, LocaleDateWords>();

/** The key of a matched word: lower case, no trailing dot, one space between words. */
const wordKey = (word: string): string => bareWord(word).replace(/\s+/g, ' ');

/** The date words and the mention pattern of one language (not English: the patterns above). */
function localeDateWords(locale: KitTextLocale): LocaleDateWords {
  const cached = localeDateWordsCache.get(locale);
  if (cached !== undefined) {
    return cached;
  }
  const words = kitWords(locale);
  const months = new Map<string, MonthWord>();
  const addMonths = (list: readonly string[], long: boolean): void => {
    list.forEach((word, index) => {
      const key = wordKey(word);
      if (!months.has(key)) {
        months.set(key, { index, long, dotted: word.endsWith('.') });
      }
    });
  };
  addMonths(words.monthsShort, false);
  addMonths(words.monthsLong, true);
  const weekdays = new Map<string, WeekdayWord>();
  const addWeekdays = (list: readonly string[], short: boolean): void => {
    list.forEach((word, index) => {
      const key = wordKey(word);
      if (!weekdays.has(key)) {
        weekdays.set(key, { index, forms: list, short });
      }
    });
  };
  addWeekdays(words.weekdaysShort, true);
  addWeekdays(words.weekdaysLong, false);
  for (const forms of KIT_WEEKDAY_CAPTION_FORMS[locale] ?? []) {
    addWeekdays(forms, false);
  }
  // The longest word first, so "października" wins over a shorter word of the same start and
  // "w sobotę" wins over "sobotę".
  const alternation = (keys: Iterable<string>): string =>
    [...keys]
      .sort((a, b) => b.length - a.length)
      .map((key) => escapeRegExp(key).replace(/ /g, '\\s+'))
      .join('|');
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(?:(${alternation(weekdays.keys())})\\.?(,)?\\s+)?(\\d{1,2})\\.?\\s+(?:(de)\\s+)?(${alternation(
      months.keys()
    )})(\\.)?(?![\\p{L}])`,
    'giu'
  );
  const result = { months, weekdays, pattern };
  localeDateWordsCache.set(locale, result);
  return result;
}

/** The new word in the case of the old word: "Sobotę" → "Wtorek". */
function inCaseOf(oldWord: string, newWord: string): string {
  const first = oldWord.charAt(0);
  return first !== first.toLocaleLowerCase() ? capitalizeKitWord(newWord) : newWord;
}

/** The mentions in the forms of one language ("16. Okt.", "pt. 16 paź"), with no overlap with `found`. */
function localeMentionsOf(text: string, locale: KitTextLocale, found: readonly Mention[]): Mention[] {
  const { months, weekdays, pattern } = localeDateWords(locale);
  const words = kitWords(locale);
  const result: Mention[] = [];
  for (const match of text.matchAll(pattern)) {
    const [whole, weekdayWord, comma, day, de, monthWord, monthDot] = match;
    const index = match.index ?? 0;
    const month = months.get(wordKey(monthWord ?? ''));
    const weekday = weekdayWord === undefined ? undefined : weekdays.get(wordKey(weekdayWord));
    const dayMonth = month === undefined ? null : dayMonthOf(month.index, Number(day));
    const overlaps = found.some((one) => index < one.index + one.length && one.index < index + whole.length);
    if (dayMonth === null || overlaps || month === undefined) {
      continue;
    }
    // A dot after a month word with no dot of its own ends the sentence ("16 de out."): it stays.
    const sentenceDot = monthDot !== undefined && !month.dotted ? '.' : '';
    result.push({
      index,
      length: whole.length,
      dayMonth,
      rewrite: (date) => {
        const newMonth = Number(date.slice(5, 7)) - 1;
        const newDay = Number(date.slice(8, 10));
        const shortLabel = words.dateLabel(newDay, newMonth);
        const dayPart = shortLabel.slice(0, shortLabel.indexOf(' '));
        const monthPart = month.long ? (words.monthsLong[newMonth] ?? '') : (words.monthsShort[newMonth] ?? '');
        const datePart = `${dayPart} ${de === undefined ? '' : `${de} `}${monthPart}`;
        const withDot = (value: string): string => (value.endsWith('.') ? value : `${value}${sentenceDot}`);
        if (weekday === undefined || weekdayWord === undefined) {
          return withDot(datePart);
        }
        const newWeekday = weekdayOf(date);
        if (weekday.short && !month.long && de === undefined) {
          // The form of the builders: the day label of the language.
          return withDot(words.dayLabel(newWeekday, newDay, newMonth));
        }
        const weekdayPart = inCaseOf(weekdayWord, weekday.forms[newWeekday] ?? '');
        return withDot(`${weekdayPart}${comma ?? ''} ${datePart}`);
      },
    });
  }
  return result;
}

function mentionsOf(text: string, locale?: KitTextLocale | null): Mention[] {
  const found: Mention[] = [];
  for (const match of text.matchAll(DAY_FIRST)) {
    const [whole, weekday, weekdayTail, day, suffix, month] = match;
    const dayMonth = dayMonthOf(monthIndexOf(month ?? ''), Number(day));
    if (dayMonth !== null) {
      const form = {
        weekday,
        longWeekday: weekdayTail !== undefined,
        suffix: suffix !== undefined,
        longMonth: (month ?? '').length > 3,
      };
      found.push({
        index: match.index ?? 0,
        length: whole.length,
        dayMonth,
        rewrite: (date) => {
          const parts = dateParts(date, form);
          const comma = weekday !== undefined && whole.includes(',') ? ',' : '';
          const lead = parts.weekday === '' ? '' : `${parts.weekday.trimEnd()}${comma} `;
          return `${lead}${parts.day} ${whole.toLowerCase().includes(' of ') ? 'of ' : ''}${parts.month}`;
        },
      });
    }
  }
  for (const match of text.matchAll(MONTH_FIRST)) {
    const [whole, weekday, weekdayTail, month, day, suffix] = match;
    const index = match.index ?? 0;
    const dayMonth = dayMonthOf(monthIndexOf(month ?? ''), Number(day));
    const overlaps = found.some((one) => index < one.index + one.length && one.index < index + whole.length);
    if (dayMonth !== null && !overlaps) {
      const form = {
        weekday,
        longWeekday: weekdayTail !== undefined,
        suffix: suffix !== undefined,
        longMonth: (month ?? '').length > 3,
      };
      found.push({
        index,
        length: whole.length,
        dayMonth,
        rewrite: (date) => {
          const parts = dateParts(date, form);
          const comma = weekday !== undefined && whole.includes(',') ? ',' : '';
          return `${parts.weekday.trimEnd()}${parts.weekday === '' ? '' : `${comma} `}${parts.month} ${parts.day}`;
        },
      });
    }
  }
  for (const match of text.matchAll(ISO)) {
    const [whole, , month, day] = match;
    const dayMonth = dayMonthOf(Number(month) - 1, Number(day));
    if (dayMonth !== null) {
      found.push({ index: match.index ?? 0, length: whole.length, dayMonth, rewrite: (date) => date.slice(0, 10) });
    }
  }
  if (locale === undefined || locale === null || locale === 'en') {
    return found.sort((a, b) => a.index - b.index);
  }
  // The forms of the language win over an English read of the same words ("1 nov." in French).
  const own = localeMentionsOf(text, locale, []);
  const english = found.filter((one) =>
    own.every((mention) => one.index + one.length <= mention.index || mention.index + mention.length <= one.index)
  );
  return [...own, ...english].sort((a, b) => a.index - b.index);
}

/**
 * The days that a text names, in the order of the text. `locale`: the language of the kit texts
 * (`plan.textLocale`); its own forms match too ("16. Okt." in German). Absent: English only. Pure.
 */
export function dateMentionsOf(text: string, locale?: KitTextLocale | null): DayMonth[] {
  return mentionsOf(text, locale).map((mention) => mention.dayMonth);
}

/** The word that takes the place of a date when the song has no release date now (English; `kitWords().soonWord` per language). */
export const NO_DATE_WORD = 'soon';

/**
 * The text with each mention of one of the `oldDays` written for the new release date, in the form
 * of the mention ("drops 1 Nov" → "drops 15 Oct", "November 1st" → "October 15th"). A mention of
 * another day stays (a caption may name a show). With no new date the mention becomes "soon". Pure.
 */
export function rewriteDateMentions(
  text: string,
  oldDays: ReadonlyArray<DayMonth>,
  releaseDate: string | null,
  locale?: KitTextLocale | null
): string {
  const mentions = mentionsOf(text, locale).filter((mention) => oldDays.includes(mention.dayMonth));
  const soon = locale === undefined || locale === null ? NO_DATE_WORD : kitWords(locale).soonWord;
  let result = text;
  for (const mention of [...mentions].reverse()) {
    const replacement = releaseDate === null ? soon : mention.rewrite(releaseDate);
    result = `${result.slice(0, mention.index)}${replacement}${result.slice(mention.index + mention.length)}`;
  }
  return result;
}

/** "1 Nov" for `11-01`. Pure. */
export function dayMonthLabel(dayMonth: DayMonth): string {
  const [month, day] = dayMonth.split('-').map(Number);
  return `${day ?? 0} ${MONTH_SHORT[(month ?? 1) - 1] ?? ''}`;
}

/* ----------------------------------------------------------------------------------------------
 * The answers of the routes.
 * -------------------------------------------------------------------------------------------- */

/** Why a file with the old date is not made again. */
export type DateRedoSkipReason =
  /** Option A: the release day has passed and the purpose of the file is before it. */
  | 'beforeRelease'
  /** The cover of the file is out of date too: the cover redo makes it again with the new date. */
  | 'coverRedo'
  /** A new version of this file is being made now: it reads the new date at its plan. */
  | 'busy'
  /** The file reads the song, and the song has no ready master. */
  | 'needsMaster'
  | 'masterProcessing'
  /** No stored plan is left to make it from. */
  | 'failed';

/** One kit asset whose texts show an old release date. */
export interface DateRedoItem {
  uuid: string;
  kind: CoverRedoKind;
  themeId: StudioThemeId;
  variant: string;
  /** The old date as the file shows it ("1 Nov"); null when the file names no day. */
  oldDate: string | null;
  /** A picture of the file as it is now (the poster of a video, else the image), or null. */
  thumbUrl: string | null;
  /** Set when the redo leaves this file out. */
  skipReason: DateRedoSkipReason | null;
}

export interface DateRedoState {
  /** True when a file to make again exists (`total` > 0). */
  outOfDate: boolean;
  /** The files that the redo makes again (no `skipReason`). */
  total: number;
  /** The release date of the song now (`YYYY-MM-DD`), or null. */
  releaseDate: string | null;
  /** The new date as the files show it ("15 Oct"), or null with no date. */
  newDateLabel: string | null;
  /** The old dates that the files show ("1 Nov"), in the order of the files. */
  oldDateLabels: string[];
  /** True from the release day on: the "Before release" files are not made again. */
  released: boolean;
  /** Every file with an old date, the skipped ones too. */
  items: DateRedoItem[];
  /** The posts that did not go out and use a file of the redo. */
  scheduledPosts: CoverRedoPost[];
  /** The posts that went out with a file of the redo. They stay as they are. */
  publishedPostCount: number;
  /** The newest date redo of the song, or null. `failedCredits` is always 0: the redo is free. */
  run: CoverRedoRun | null;
}

export interface DateRedoRequest {
  /** True: the posts that did not go out move to the new files when each file is ready. */
  swapPosts: boolean;
  dryRun?: boolean;
}

export interface DateRedoResult {
  runId: string | null;
  /** The new kit asset versions that are queued. */
  queued: number;
  skipped: Array<{ uuid: string; reason: DateRedoSkipReason }>;
  /** The posts that move to the new files when they are ready (0 when `swapPosts` is false). */
  postsToSwap: number;
}

/** The files of the redo that are made again. Pure. */
export function dateRedoItemsToMake(items: readonly DateRedoItem[]): DateRedoItem[] {
  return items.filter((item) => item.skipReason === null);
}

/** The files of the "Before release" group (option A). Pure. */
export function beforeReleaseItems(items: readonly DateRedoItem[]): DateRedoItem[] {
  return items.filter((item) => item.skipReason === 'beforeRelease');
}
